const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function fetchAPI(url: string, options: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });

  if (response.status === 204) {
    return null;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData['hydra:description'] || errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export interface CourseCatalog {
  '@id': string;
  id: number;
  name: string;
}

export interface Course {
  '@id': string;
  id: number;
  title: string;
  slug: string;
  description?: string;
  thumbnailUrl?: string;
  estimatedDuration?: number;
  difficulty: string;
  tags?: string[];
  catalog?: CourseCatalog;
  enrollmentMode?: string;
  isLicensedReference?: boolean;
}

export interface Recommendation {
  course: Course;
  score: number;
  reasons: string[];
  reasonLabels: string[];
  enrollmentMode?: string;
  isLicensedReference?: boolean;
}

export interface RecommendationsResponse {
  recommendations: Recommendation[];
  count: number;
}

export interface SimilarCoursesResponse {
  courseId: number;
  similar: Recommendation[];
  count: number;
}

class RecommendationService {
  /**
   * Get personalized course recommendations
   */
  async getRecommendations(limit: number = 5, locale: string = 'de'): Promise<Recommendation[]> {
    const response: RecommendationsResponse = await fetchAPI(
      `${API_BASE_URL}/api/courses/recommendations?limit=${limit}&locale=${locale}`
    );
    return response.recommendations || [];
  }

  /**
   * Get similar courses to a specific course
   */
  async getSimilarCourses(courseId: number, limit: number = 5, locale: string = 'de'): Promise<Recommendation[]> {
    const response: SimilarCoursesResponse = await fetchAPI(
      `${API_BASE_URL}/api/courses/${courseId}/similar?limit=${limit}&locale=${locale}`
    );
    return response.similar || [];
  }

  /**
   * Get the first reason label (most important reason)
   */
  getPrimaryReason(recommendation: Recommendation): string | null {
    return recommendation.reasonLabels?.[0] || null;
  }

  /**
   * Format duration in minutes to human-readable string
   */
  formatDuration(minutes: number | undefined): string {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  }

  /**
   * Get difficulty label
   */
  getDifficultyLabel(difficulty: string): string {
    const labels: Record<string, string> = {
      'beginner': 'Einstieg',
      'intermediate': 'Vertiefung',
      'advanced': 'Spezialisierung'
    };
    return labels[difficulty] || difficulty;
  }

  /**
   * Get color for difficulty chip
   */
  getDifficultyColor(difficulty: string): 'success' | 'warning' | 'error' {
    switch (difficulty) {
      case 'beginner':
        return 'success';
      case 'intermediate':
        return 'warning';
      case 'advanced':
        return 'error';
      default:
        return 'success';
    }
  }

  /**
   * Get icon for reason
   */
  getReasonIcon(reason: string): string {
    const icons: Record<string, string> = {
      'same_catalog': 'folder',
      'next_difficulty': 'trending_up',
      'same_difficulty': 'trending_flat',
      'matching_tags': 'local_offer',
      'prerequisites_met': 'check_circle',
      'popular': 'star',
      'beginner_friendly': 'school'
    };
    return icons[reason] || 'auto_awesome';
  }
}

export const recommendationService = new RecommendationService();
export default recommendationService;
